// Ortak sayı/tarih yardımcıları — Nebim parser'ındaki toNumber ile aynı
// mantık; farklı dosya içinde tutuldu ki Nebim modülüne dokunulmasın.

/** Türkçe + en-US sayı formatlarını güvenli şekilde ayrıştırır.
 *  "1.999,09" → 1999.09, "1,00" → 1, "2.199" → 2199, "1,234.56" → 1234.56. */
export function toNumber(v: unknown): number | null {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  let s = String(v).trim();
  if (!s) return null;
  s = s.replace(/[%\s₺$€£]/g, "");
  if (!s) return null;
  let sign = 1;
  if (/^\(.*\)$/.test(s)) {
    sign = -1;
    s = s.slice(1, -1);
  }
  if (!/[0-9]/.test(s)) return null;

  const hasComma = s.includes(",");
  const dotCount = (s.match(/\./g) || []).length;

  let normalized: string;
  if (hasComma && dotCount > 0) {
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    if (lastComma > lastDot) {
      normalized = s.replace(/\./g, "").replace(",", ".");
    } else {
      normalized = s.replace(/,/g, "");
    }
  } else if (hasComma) {
    const commaCount = (s.match(/,/g) || []).length;
    if (commaCount > 1) normalized = s.replace(/,/g, "");
    else normalized = s.replace(",", ".");
  } else if (dotCount > 1) {
    normalized = s.replace(/\./g, "");
  } else if (dotCount === 1) {
    const dotIdx = s.lastIndexOf(".");
    const digitsAfter = s.length - dotIdx - 1;
    if (digitsAfter === 3) normalized = s.replace(".", "");
    else normalized = s;
  } else {
    normalized = s;
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? sign * n : null;
}

/** Tarihin gerçekten var olduğunu doğrular; geçersiz ham metni otomatik ISO tarihe dönüştürmez. */
function toIsoDate(year: number, month: number, day: number): string | null {
  if (!Number.isInteger(year) || year < 1 || year > 9999 ||
      !Number.isInteger(month) || month < 1 || month > 12 ||
      !Number.isInteger(day) || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 ||
      date.getUTCDate() !== day) return null;
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Türkçe tarih biçimlerinden ISO yyyy-mm-dd üretir. Çıkaramazsa ham string döner. */
export function parseInvoiceDate(v: unknown): string | null {
  if (v == null || v === "") return null;
  if (v instanceof Date) {
    if (!Number.isFinite(v.getTime())) return null;
    const iso = toIsoDate(v.getUTCFullYear(), v.getUTCMonth() + 1, v.getUTCDate());
    return iso ?? null;
  }
  const s = String(v).trim();
  if (!s) return null;

  // ISO biçimini önce ele al.
  const isoMatch = s.match(/^([0-9]{4})-([0-9]{1,2})-([0-9]{1,2})$/);
  if (isoMatch) {
    const iso = toIsoDate(Number(isoMatch[1]), Number(isoMatch[2]), Number(isoMatch[3]));
    return iso ?? s;
  }

  // Türkçe: 01.10.2024, 01/10/2024, 01-10-2024
  const localMatch = s.match(/^([0-9]{1,2})[./-]([0-9]{1,2})[./-]([0-9]{2,4})$/);
  if (localMatch) {
    const day = Number(localMatch[1]);
    const month = Number(localMatch[2]);
    let year = Number(localMatch[3]);
    if (localMatch[3].length === 2) year = year > 70 ? 1900 + year : 2000 + year;
    return toIsoDate(year, month, day) ?? s;
  }
  return s; // ham metni koru — kullanıcı önizlemede düzeltir
}

/** SHA-256 dosya özeti. */
export async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", buf);
  const bytes = new Uint8Array(hash);
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, "0");
  }
  return out;
}

/** Metni Türkçe diakritiksiz, snake_case anahtara çevirir — başlık eşlemesi için. */
export function normalizeHeader(s: string): string {
  const TR_MAP: Record<string, string> = {
    ı: "i", İ: "i", ş: "s", Ş: "s", ğ: "g", Ğ: "g",
    ü: "u", Ü: "u", ö: "o", Ö: "o", ç: "c", Ç: "c",
  };
  return s
    .toString()
    .replace(/[ıİşŞğĞüÜöÖçÇ]/g, (c) => TR_MAP[c] ?? c)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\./g, "")
    .replace(/\(([^)]+)\)/g, "_$1")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
}


/** True only for a real ISO calendar date (not an unvalidated raw parser string). */
export function isValidIsoDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value) || Number(value.slice(0, 4)) < 1) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
